import { Product, SkuOption } from "@/api/product-api";
import { PRODUCT_COLOR_CONFIG } from "@/utils/product-colors";

// TactGloveOptions.tsx 의 SIZE_OPTIONS 와 동일. 라벨은 Notify Me 모달용 풀네임.
const SIZE_OPTIONS = ["small", "medium", "large", "x-large"] as const;
const SIZE_LABELS: Record<string, string> = {
  small: "Small",
  medium: "Medium",
  large: "Large",
  "x-large": "X-Large",
};
const SIZE_PRIORITY: Record<string, number> = {
  small: 0,
  medium: 1,
  large: 2,
  "x-large": 3,
};

const KNOWN_COLORS = Object.keys(PRODUCT_COLOR_CONFIG);
const KNOWN_SIZES = SIZE_OPTIONS as readonly string[];

// "어떤 컬러든" 노티 의사를 나타내는 sentinel 값 — 일반 컬러 키와 구분되도록 prefix 사용
export const ALL_COLORS_VALUE = "__all_colors__";
/**
 * 「All sizes」 — 사이즈만 있는 제품이 **전부 품절**일 때 품절 사이즈 전부에 신청한다(`ALL_COLORS_VALUE` 의
 * 사이즈판). 이너글러브 사이즈 라인의 「Notify me」 가 이 경우다(#5922).
 */
export const ALL_SIZES_VALUE = "__all_sizes__";

function findColorInOptions(options: string[]): string | undefined {
  return options.find((opt) =>
    KNOWN_COLORS.some((c) => opt.toLowerCase().includes(c)),
  );
}

function findSizeInOptions(options: string[]): string | undefined {
  return options.find((opt) => KNOWN_SIZES.includes(opt.toLowerCase()));
}

export interface NotifyMeOption {
  value: string;
  label: string;
}

export interface NotifyMeOptionConfig {
  hasColorVariant: boolean;
  hasSizeVariant: boolean;
  // Color dropdown: sold-out 컬러만 노출
  soldOutColors: NotifyMeOption[];
  // Size dropdown: 선택된 컬러에 따라 동적으로 결정됨. getSoldOutSizesForColor 사용.
  // "All colors" 옵션은 알려진 모든 컬러가 어딘가 품절일 때만 추가
  showAllColors: boolean;
  // "All sizes" — 색상 변형이 없고 사이즈 2개 이상이 **전부 품절**일 때(`showAllColors` 의 사이즈판, #5922).
  // 색상 있는 제품(글러브)은 색상 → 사이즈 순으로 고르므로 켜지 않는다.
  showAllSizes: boolean;
}

export function getNotifyMeOptions(
  product: Product | undefined,
): NotifyMeOptionConfig {
  const empty: NotifyMeOptionConfig = {
    hasColorVariant: false,
    hasSizeVariant: false,
    soldOutColors: [],
    showAllColors: false,
    showAllSizes: false,
  };
  if (!product) return empty;

  const skuOptions = product.skuOptions ?? [];
  if (skuOptions.length === 0) return empty;

  const allColors = new Set<string>();
  const allSizes = new Set<string>();
  const soldOutColorSet = new Set<string>();
  // 컬러별 "재고 있는 SKU 가 하나라도 있는지" 추적 — "완전 품절" 판정에 사용
  const colorHasAnyStock: Record<string, boolean> = {};
  const sizeHasAnyStock: Record<string, boolean> = {};

  for (const opt of skuOptions) {
    const color = findColorInOptions(opt.options);
    const size = findSizeInOptions(opt.options);
    if (color) {
      allColors.add(color);
      if ((opt.stock ?? 0) > 0) colorHasAnyStock[color] = true;
    }
    if (size) {
      allSizes.add(size);
      if ((opt.stock ?? 0) > 0) sizeHasAnyStock[size] = true;
    }
    if ((opt.stock ?? 0) <= 0) {
      if (color) soldOutColorSet.add(color);
    }
  }

  const hasColorVariant = allColors.size > 0;
  const hasSizeVariant = allSizes.size > 0;

  // "All colors" 는 알려진 모든 컬러가 "완전히 품절" 일 때만 (어떤 사이즈도 재고 0)
  const showAllColors =
    hasColorVariant &&
    allColors.size > 1 &&
    [...allColors].every((c) => !colorHasAnyStock[c]);

  const showAllSizes =
    !hasColorVariant &&
    allSizes.size > 1 &&
    [...allSizes].every((s) => !sizeHasAnyStock[s]);

  const soldOutColors: NotifyMeOption[] = [...soldOutColorSet]
    .map((color) => ({
      value: color,
      // 색상명만 capitalize 해서 라벨로 (예: "charcoal" → "Charcoal")
      label: color.charAt(0).toUpperCase() + color.slice(1),
    }))
    .sort((a, b) => a.value.localeCompare(b.value));

  return {
    hasColorVariant,
    hasSizeVariant,
    soldOutColors,
    showAllColors,
    showAllSizes,
  };
}

// 선택된 컬러에 따라 sold-out 사이즈 목록을 반환.
// color === ALL_COLORS_VALUE 이면 모든 컬러에서 sold-out 인 사이즈만 반환 (교집합).
export function getSoldOutSizesForColor(
  product: Product | undefined,
  selectedColor: string | undefined,
): NotifyMeOption[] {
  if (!product) return [];
  const skuOptions = product.skuOptions ?? [];
  if (skuOptions.length === 0) return [];

  const colorsInProduct = new Set<string>();
  for (const opt of skuOptions) {
    const c = findColorInOptions(opt.options);
    if (c) colorsInProduct.add(c);
  }

  const sizeSet = new Set<string>();

  // ★**색상 변형이 아예 없는 제품**(사이즈만 — 이너글러브 사이즈 라인 등)은 컬러 선택을 기다리지 않는다.
  // 종전엔 아래 「컬러 미선택 → 빈 목록」에 걸려 사이즈 칸이 그려지지 않았는데, 스키마는 사이즈를
  // 필수로 요구해 **고를 칸 없이 「Please select a size」로 막혔다**(#5922 — 그때까지 사이즈만 있는
  // 제품이 없어 드러나지 않았다). 색상이 있는 제품은 이 갈래를 타지 않는다.
  if (colorsInProduct.size === 0) {
    for (const opt of skuOptions) {
      const size = findSizeInOptions(opt.options);
      if (size && (opt.stock ?? 0) <= 0) sizeSet.add(size);
    }
  } else if (!selectedColor) {
    // 컬러 미선택 시엔 빈 리스트 (사용자가 컬러 먼저 선택하도록 유도)
    return [];
  } else if (selectedColor === ALL_COLORS_VALUE) {
    // "All colors" 의 size 목록 = 모든 컬러에서 sold-out 인 사이즈 (교집합).
    // 예: Black-L OOS, Brown-L OOS, Charcoal-L OOS 면 L 노출
    const sizeToSoldOutColors: Record<string, Set<string>> = {};
    for (const opt of skuOptions) {
      const color = findColorInOptions(opt.options);
      const size = findSizeInOptions(opt.options);
      if (!color || !size) continue;
      if ((opt.stock ?? 0) <= 0) {
        if (!sizeToSoldOutColors[size]) sizeToSoldOutColors[size] = new Set();
        sizeToSoldOutColors[size].add(color);
      }
    }
    for (const [size, colors] of Object.entries(sizeToSoldOutColors)) {
      if (colors.size === colorsInProduct.size) sizeSet.add(size);
    }
  } else {
    // 특정 컬러의 sold-out 사이즈만
    for (const opt of skuOptions) {
      const color = findColorInOptions(opt.options);
      const size = findSizeInOptions(opt.options);
      if (color !== selectedColor || !size) continue;
      if ((opt.stock ?? 0) <= 0) sizeSet.add(size);
    }
  }

  return [...sizeSet]
    .map((size) => ({ value: size, label: SIZE_LABELS[size] ?? size }))
    .sort(
      (a, b) =>
        (SIZE_PRIORITY[a.value] ?? Infinity) -
        (SIZE_PRIORITY[b.value] ?? Infinity),
    );
}

// 선택된 color/size 조합에 매칭되는 SKU 들을 반환.
// color === ALL_COLORS_VALUE 이면 해당 size 의 sold-out color SKU 들 전부 반환 (다중 호출용).
export function resolveSkusForNotification(
  skuOptions: SkuOption[],
  selectedColor: string | undefined,
  selectedSize: string | undefined,
): string[] {
  const matches = skuOptions.filter((opt) => {
    const optColor = findColorInOptions(opt.options);
    const optSize = findSizeInOptions(opt.options);

    if (
      selectedSize &&
      selectedSize !== ALL_SIZES_VALUE &&
      optSize !== selectedSize
    )
      return false;
    if (selectedColor && selectedColor !== ALL_COLORS_VALUE) {
      if (optColor !== selectedColor) return false;
    }
    return (opt.stock ?? 0) <= 0; // 품절 SKU 만
  });

  return matches.map((m) => m.sku);
}

/**
 * 재입고 알림을 **어느 (제품 slug, SKU) 들로 신청할지** — `NotifyMe` 의 제출이 쓰는 단일 판정.
 *
 * 종전엔 `NotifyMe` 의 `onSubmit` 안에 인라인이었다. 동작은 그대로다:
 * - 색·사이즈 변형이 있으면 고른 조합의 **품절 SKU** 들(없으면 `productSku` 하나로 폴백)
 * - 변형이 없으면 `productSku` 하나
 *
 * ★`slugBySku` — SKU 마다 **다른 제품**일 때(사이즈 라인: 이너글러브 S/M/L/XL 이 각각 별도 제품) 그 SKU 를
 * 가진 제품의 slug 로 신청한다. 없으면 전부 `productSlug`(종전과 같음). 이게 없으면 M 을 골라도 알림이
 * 대표(S) 제품 slug 로 걸린다(#5922).
 */
export function resolveNotificationTargets({
  product,
  productSlug,
  productSku,
  color,
  size,
  slugBySku,
}: {
  product: Product | undefined;
  productSlug: string;
  productSku: string;
  color: string | undefined;
  size: string | undefined;
  slugBySku?: Record<string, string>;
}): { productSlug: string; sku: string }[] {
  const config = getNotifyMeOptions(product);
  const useVariants = config.hasColorVariant || config.hasSizeVariant;
  if (!useVariants || !product) return [{ productSlug, sku: productSku }];

  const skus = resolveSkusForNotification(
    product.skuOptions ?? [],
    color,
    size,
  );
  // 매칭되는 품절 SKU 가 없으면 fallback 으로 productSku/slug 사용
  if (skus.length === 0) return [{ productSlug, sku: productSku }];
  // "All colors" 케이스: 사이즈에 해당하는 sold-out color SKU 각각
  return skus.map((sku) => ({
    productSlug: slugBySku?.[sku] ?? productSlug,
    sku,
  }));
}
